// Main EventReach engine

// Return true if obj is empty
const isEmpty = obj => [Object, Array].includes((obj || {}).constructor) && !Object.entries((obj || {})).length;

// Return true if the value is blank.
//
// A blank value is undefined, null, an empty string, an empty array, an empty object (no keys)
// or a string composed entirely of spaces
//
// Roughly speaking, this is the same behavior as Rails' #blank?. Note that 0 is not blank.
const isBlank = function(value) {
  if ((value === undefined) || (value === null)) { return true; }

  if (typeof(value) === 'string') {
    return !!value.match(/^\s*$/);
  } else if (typeof(value) === 'number') {
    return false;
  } else if (typeof(value) === 'boolean') {
    return !value;
  }
  return isEmpty(value);
};

// @private
//
// Rule Evaluator
//
// Calls the `evaluate` function defined on the rule
export class RuleEvaluator {
  // Rule configuration data
  //
  // @see EventReachEngine.rules
  rule = null;

  // Platform-specific rule configuration settings
  platform_config = null;

  // Evaluated score for this rule
  score = 0;

  // Maximum score available for this rule
  max_score = 1;

  // Result of evaluation status
  status = null;

  // Is this rule required for completion
  required = true;

  // Is this a pre-requiste rule (a rule that is required for the score to be generated)
  prereq = false;

  // Is this rule hidden
  hidden = false;

  // Initialize an instance of the RuleEvaluator
  //
  // @param rule [Object] The rule configuration data
  // @param platform_config [Object] Platform-specific rule configuration data
  //
  // Each rule has a type:
  // * `presence`: Checks for the presence of the specified field
  // * `presence_any`: Checks for the presence of at least one of the specified fields
  // * `custom`: Runs the function defined in +test+
  //
  // @option rule type [String] The type of rule this is, one of `presence`, `presence_any`, or `custom`
  // @option rule required [Boolean] `true` if this a required rule. An optional rule does not contribute
  //   to the resulting score. (default: `true`)
  // @option rule prereq [Boolean] `true` if this is a required rule for the score to be generated (default: `false`)
  // @option rule score [Number] The maximum number of points this rule contributes to the score. (default: `1`)
  // @option rule field [String] For `presence` rules, the attribute whose presence is checked
  // @option rule fields [Array<String>] For `presence_any` rules, the list of attributes of which at least
  //   one must be present.
  // @option rule condition [Function] Function that returns true if this rule should be evaluated as part
  //   of the run. Used to conditionally enable rules based on platform configuration.
  // @option rule evaluate [Function] For `custom` rules, the evaluation function to run. Expected to return
  //   an object `{score: N, status: "satisfied"|"failed"|"ignored"}` based on the results of the
  //   test. `N` is the number of points (out of the maximum) that were awarded.
  constructor(rule, platform_config) {
    this.enabled = this.enabled.bind(this);
    this.evaluate = this.evaluate.bind(this);
    this._setResult = this._setResult.bind(this);
    this.rule = rule;
    this.platform_config = platform_config;
    this.max_score = (this.rule.score != null) ? this.rule.score : 1;
    this.required = (this.rule.required != null) ? this.rule.required : true;
    this.prereq = (this.rule.prereq != null) ? this.rule.prereq : false;
    this.hidden = (this.rule.hidden != null) ? this.rule.hidden : false;
  }

  // Returns true if this rule is enabled
  //
  // @return [Boolean] true if the rule is enabled
  enabled() {
    if (this.rule.condition != null) {
      return this.rule.condition.call(this);
    }
    return true;
  }

  // Evaluate this rule
  //
  // @param event_data [Object] The event data to evaluate the rule against
  // @return [Object] result with the following properties: `score`: number of points,
  //   `status`: one of `satisfied`, `partial`, `failed`, or `ignored` based on the success result
  evaluate(event_data) {
    return this._setResult(this.rule.evaluate.call(this, event_data));
  }

  // Set the result of this rule
  //
  // Returns the result itself
  _setResult(result) {
    this.status = result.status;
    switch (result.status) {
      case 'satisfied': case 'partial': this.score = result.score; break;
      default: this.score = 0;
    }
    return result;
  }
}

// @private
//
// Evaluates a presence rule
export class PresenceRuleEvaluator extends RuleEvaluator {
  // Field to check for presence of
  field = null;

  // Initialize an instance of PresenceRuleEvaluator
  //
  // @throw [TypeError] if `field` is not specified in the rule
  constructor(rule, platform_config) {
    super(rule, platform_config);
    this.evaluate = this.evaluate.bind(this);
    if (rule.field == null) { throw new TypeError("field must be specified for presence rules"); }
    this.field = rule.field;
  }

  // Check for the presence of `field` on the `event` data
  evaluate(event_data) {
    const result = isBlank(event_data[this.field]) ?
      // blank
      {score: 0, status: this.required ? 'failed' : 'ignored'}
    :
      {score: this.max_score, status: 'satisfied'};

    return this._setResult(result);
  }
}

// @private
//
// Evaluates a presence_any rule
export class PresenceAnyRuleEvaluator extends RuleEvaluator {
  // Fields to check for presence of
  fields = null;

  // Initialize an instance of PresenceAnyRuleEvaluator
  //
  // @throw [TypeError] if `fields` is not specified or is empty in the rule
  constructor(rule, platform_config) {
    super(rule, platform_config);
    this.evaluate = this.evaluate.bind(this);

    if ((rule.fields == null) || (rule.fields.length === 0)) {
      throw new TypeError("fields must be specified for presence_any rules");
    }

    this.fields = rule.fields;
  }

  // Check for the presence of at least one field in `fields` on the `event` data
  evaluate(event_data) {
    let result = {score: 0, status: this.required ? 'failed' : 'ignored'};

    for (const field of Array.from(this.fields)) {
      if (!isBlank(event_data[field])) { result = {score: this.max_score, status: 'satisfied'}; }
    }

    return this._setResult(result);
  }
}

// @private
//
// Evaluates a presence_all rule
export class PresenceAllRuleEvaluator extends RuleEvaluator {
  // Fields to check for presence of
  fields = null;

  // Initialize an instance of PresenceAllRuleEvaluator
  //
  // @throw [TypeError] if `fields` is not specified or is empty in the rule
  constructor(rule, platform_config) {
    super(rule, platform_config);
    this.evaluate = this.evaluate.bind(this);

    if ((rule.fields == null) || (rule.fields.length === 0)) {
      throw new TypeError("fields must be specified for presence_all rules");
    }

    this.fields = rule.fields;
  }

  // Check that all fields in `fields` are present on the `event` data
  evaluate(event_data) {
    let result = {score: this.max_score, status: 'satisfied'};

    for (const field of Array.from(this.fields)) {
      if (isBlank(event_data[field])) { result = {score: 0, status: this.required ? 'failed' : 'ignored'}; }
    }

    return this._setResult(result);
  }
}

// EventReach calculation engine
//
// Given platform_config and event data, evaluates each rule and returns the score for
// the given event.
export class EventReachEngine {
  // List of rules to evaluate for the score
  //
  // Each key represents a specific rule. The key is returned in the evaluation
  // results, along with its evaluated state.
  //
  // @see RuleEvaluator#constructor
  rules = {
    // Presence (single field) checks
    name: {
      type: 'presence',
      score: 0,
      prereq: true,
      field: 'name',
      hidden: true
    },
    date_prereq: {
      type: 'presence',
      score: 0,
      prereq: true,
      field: 'instance_dates',
      hidden: true
    },
    hashtag: {
      type: 'presence',
      field: 'hashtag'
    },
    ticket_url: {
      type: 'presence',
      field: 'ticket_url',
      required: false
    },
    allows_reviews: {
      type: 'presence_all',
      fields: ['allows_reviews', 'allows_attendance']
    },
    // Presence any (one of multiple) checks
    keywords_or_tags: {
      type: 'presence_any',
      fields: ['keywords', 'tags']
    },
    facebook_or_url: {
      type: 'presence_any',
      fields: ['facebook_id', 'url']
    },
    featured_or_sponsored: {
      type: 'presence_any',
      fields: ['is_sponsored', 'featured_sections'],
      required: false
    },
    photo: {
      type: 'presence_any',
      fields: ['has_photo', 'photo_id']
    },
    included_in_trending: {
      type: 'presence',
      field: 'included_in_trending'
    },
    place: {
      type: 'presence',
      field: 'business_id',
      condition() { return !!this.platform_config.has_places; }
    },
    location: {
      type: 'presence_any',
      fields: ['business_id', 'location']
    },

    // Custom rules

    filters: {
      type: 'custom',
      required: true,
      score: 1,
      evaluate(event_data) {
        const needed_filters = 1;
        let num_filters = 0;

        if (event_data.filters != null) {
          for (const key in event_data.filters) {
            const values = event_data.filters[key];
            if (!isBlank(values)) { num_filters += 1; }
          }
        }

        if (num_filters === 0) {
          return {score: 0, status: 'failed'};
        }
        return {score: 1, status: 'satisfied'};
      }
    },


    // Date is at least 7 days out
    date: {
      type: 'custom',
      required: true,
      score: 1,
      evaluate(event_data) {
        let next_instance;
        const created_at = event_data.created_at || new Date;
        if (!isEmpty(event_data.instance_dates)) { next_instance = event_data.instance_dates.reduce((function(item, currentMax) { if (item >= currentMax) { return item; }  return currentMax;  }), event_data.instance_dates[0]); }
        const diff = next_instance - created_at;

        if (diff >= (7 * 86400 * 1000)) { // 7 days in ms
          return {score: 1, status: 'satisfied'};
        }
        return {score: 0, status: 'failed'};
      }
    },

    // Description length
    description: {
      type: 'custom',
      required: true,
      score: 1,
      evaluate(event_data) {
        const length = event_data.description != null ? event_data.description.length : undefined;

        if (!isBlank(event_data.description) && (length >= 160)) {
          return {score: 1, status: 'satisfied'};
        }
        return {score: 0, status: 'failed'};
      }
    },

    // Custom prereq rule to ensure the event is visible
    visible_prereq: {
      type: 'custom',
      hidden: true,
      prereq: true,
      score: 0,
      evaluate(event_data) {
        if ((event_data.visibility === 'visible') && !event_data.hide_from_main_calendar && !event_data.visible_only_to_widget && !event_data.visible_only_to_channel) {
          return {score: 0, status: 'satisfied'};
        }
        return {score: 0, status: 'failed'};
      }
    }
  };

  // Initialize an instance of EventReachEngine with the specified `platform_config`.
  //
  // @param platform_config [Hash] Platform configuration. Passed into the rules.
  constructor(platform_config) {
    this.evaluate = this.evaluate.bind(this);
    this.prereqRules = this.prereqRules.bind(this);
    this.otherRules = this.otherRules.bind(this);
    this._ruleEvaluator = this._ruleEvaluator.bind(this);
    this._evaluateRules = this._evaluateRules.bind(this);
    this._cleanEventData = this._cleanEventData.bind(this);
    this.platform_config = platform_config || {};
  }

  // Evaluates the defined rules, returning an object containing the following keys:
  // * `score`: Points assigned to matched rules
  // * `max`: Total available points
  // * `rules`: Hash of rule_key -> {score: N, status: (`satisfied`|`failed`|`ignored`|`disabled`)} for each defined rule.
  //   "disabled" means the rule was not enabled.
  //
  // @throw [TypeError] if rule type is unknown
  evaluate(event_data) {
    let key; let rule;
    if (event_data != null) { event_data = this._cleanEventData(event_data); }

    const results = {
      score: 0,
      max: 0,
      rules: {}
    };

    // Validate the prereqs
    let prereqs_satisfied = true;
    const prereq_results = this._evaluateRules(this.prereqRules(), event_data);
    const other_results = this._evaluateRules(this.otherRules(), event_data);

    // Calculate total score and generate full results
    for (key in prereq_results) {
      rule = prereq_results[key];
      if (!rule.hidden) { results.rules[key] = rule.status; }
      if (rule.required) {
        switch (rule.status) {
          case 'satisfied': case 'partial':
            results.max += rule.max_score;
            results.score += rule.score;
            break;
          case 'failed':
            results.max += rule.max_score;
            prereqs_satisfied = false;
            break;
        }
      }
    }

    for (key in other_results) {
      rule = other_results[key];
      if (!rule.hidden) { results.rules[key] = rule.status; }
      if (rule.required) {
        switch (rule.status) {
          case 'satisfied': case 'partial':
            results.max += rule.max_score;
            results.score += rule.score;
            break;
          case 'failed':
            results.max += rule.max_score;
            break;
        }
      }

      if (!prereqs_satisfied && (results.rules[key] !== 'disabled')) { results.rules[key] = 'ignored'; }
    }

    // Force score to 0 if the prereq rules aren't satisfied
    if (!prereqs_satisfied) { results.score = 0; }

    return results;
  }

  // Return the list of prereq rules
  prereqRules() {
    const rules = {};
    for (const key in this.rules) {
      const rule_data = this.rules[key];
      if (rule_data.prereq) { rules[key] = this._ruleEvaluator(key, rule_data); }
    }
    return rules;
  }

  // Return the list of normal rules
  otherRules() {
    const rules = {};
    for (const key in this.rules) {
      const rule_data = this.rules[key];
      if (!rule_data.prereq) { rules[key] = this._ruleEvaluator(key, rule_data); }
    }
    return rules;
  }

  // Return the Rule Evaluator
  _ruleEvaluator(key, rule_data) {
    switch (rule_data.type) {
      case 'presence': return new PresenceRuleEvaluator(rule_data, this.platform_config);
      case 'presence_any': return new PresenceAnyRuleEvaluator(rule_data, this.platform_config);
      case 'presence_all': return new PresenceAllRuleEvaluator(rule_data, this.platform_config);
      case 'custom': return new RuleEvaluator(rule_data, this.platform_config);
      default:
        if (typeof console !== 'undefined' && console !== null) {
          console.error('No evaluator found for rule', key, rule_data);
        }
        throw new TypeError(`No evaluator found for rule ${key} type ${rule_data.type}`);
    }
  }

  // Evaluate a list of rules
  //
  // Takes in an object of rule key -> rule_evaluator and returns a hash of
  // rule key -> {score, max, result}
  _evaluateRules(rules, event_data) {
    for (const key in rules) {
      const rule = rules[key];
      if (rule) {
        if (rule.enabled()) {
          rule.evaluate(event_data);
        } else {
          rule.status = 'disabled';
          rule.score = 0;
        }
      }
    }

    return rules;
  }

  // Clean event data
  //
  // This converts some date strings into date objects in created_at and instance_dates
  _cleanEventData(data) {
    if ((data.created_at != null) && (typeof(data.created_at) === 'string')) { data.created_at = new Date(data.created_at); }
    if (data.instance_dates != null) {
      data.instance_dates = Array.from(data.instance_dates).map((instance) =>
        (typeof(instance) === 'string' ?
          new Date(instance)
        :
          instance));
    }
    return data;
  }
}
